<template>
    <div class="modal player-settings">
        <div class="modal-content">
            <div
                class="modal-close"
                @click="$emit('close',{
                    editPlayerType: null,
                    showEmbed: false,
                    player: null,
                })"
            >
                <i class="far fa-times" />
            </div>
            <div class="player-settings-header">
                <div class="player-title">
                    <template v-if="'archive' === playerType">
                        <base-editable-text
                            :text-class="['h3']"
                            :text="playerName"
                            @change="value => playerName = value"
                        />
                    </template>
                    <template v-else>
                        <p class="h3">
                            <featured-button
                                :track-no="trackNo"
                                :player-featured="isTrackFeatured"
                                icon-class="featured-icon"
                                :show="show"
                            />
                            {{ playerName }}
                        </p>
                    </template>
                </div>
                <tabs-nav
                    :tabs="tabs"
                    :current-tab="currentTab"
                    :container-class="['player-settings', 'player-settings-nav']"
                    @change="controlsChange"
                />
            </div>

            <div class="player-settings-content">
                <transition
                    name="fade"
                    mode="out-in"
                >
                    <keep-alive>
                        <player-embed-controler
                            v-slot:default="{
                                    playerEmbed,
                                    methods,
                                }"
                        >
                            <component
                                :is="currentTabComponent"
                                :key="currentTabComponent"
                                :show="show"
                                :player="player"
                                :player-type="playerType"
                                :player-options="player"
                                :player-name="playerName"
                                :track-no="trackNo"
                                :track-guid="trackGuid"
                                :initialize-embed="methods.initializeEmbed"
                                :set-global-config="methods.setGlobalConfig"
                                :player-embed="playerEmbed"
                                @cancel="closeModal"
                                @player-updated="closeModal"
                                @set-embed-config="value => methods.setGlobalConfig(value)"
                                @player-changed="playerChanged"
                            />
                        </player-embed-controler>
                    </keep-alive>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
import PlayerSettings from './PlayerSettings';
import EmbedCode from './EmbedCode';
import TabsNav from './TabsNav';
import FeaturedButton from '@/components/FeaturedButton';
import PlayerEmbedControler from '@/components/PlayerEmbedControler';

import dataChangeHandler from '@/utils/dataChangeHandler';
import componentName from '@/utils/componentName';
import { isEqual } from 'lodash';
import { mapState } from 'vuex';
import modal from '@/mixins/modal';

export default {
    components: {
        PlayerSettings,
        EmbedCode,
        TabsNav,
        FeaturedButton,
        PlayerEmbedControler,
    },

    mixins: [modal],

    props: {
        playerType: {
            type: String,
            required: true,
        },
        show: {
            type: Object,
            required: true,
        },
        player: {
            type: Object,
            default: () => ({}),
        },
        showEmbed: {
            type: Boolean,
            default: false,
        },
        trackNo: {
            type: String,
            default: '1:0',
        },
        trackGuid: {
            type: String,
            default: null,
        },
    },

    data: () => ({
        tabs: [
            {
                name: 'Player Settings',
                icon: 'cog',
            },
            {
                name: 'Embed Code',
                icon: 'code',
            },
        ],
        currentTab: 'Player Settings',
        playerName: '',
    }),

    computed: {
        ...mapState(['trackPlayers']),

        /**
         *
         * @returns  {boolean}  If track player is featured
         */
        isTrackFeatured() {
            return this.trackPlayers[this.show.hash][this.trackNo].featured;
        },

        /**
         *
         * @returns  {string}  Track player title
         */
        trackPlayerName() {
            return this.trackPlayers[this.show.hash][this.trackNo].title;
        },

        /**
         * Returns component path
         *
         * @returns  {string}  Component name
         */
        currentTabComponent() {
            return componentName(this.currentTab);
        },
    },

    watch: {
        player: {
            immediate: true,

            /**
             * Manage data.
             *
             * @param {object} newSettings Player object data.
             * @param {*} oldSettings Player object data.
             */
            handler(newSettings, oldSettings) {
                if (isEqual(newSettings, oldSettings)) {
                    return;
                }

                if ('archive' === this.playerType) {
                    this.playerName = newSettings.name;
                } else {
                    this.playerName = this.trackPlayerName;
                }
            },
        },
    },

    /**
     * Created hook
     */
    mounted() {
        if (this.showEmbed) {
            this.currentTab = 'Embed Code';
        }
    },

    methods: {
        /**
         * Handle controls change
         *
         * @param   {object}  changed  Changed configuation
         *
         */
        controlsChange(changed) {
            dataChangeHandler(this, changed);
        },

        /**
         * Change player settings
         *
         * @param   {object}  config  Player settings object.
         *
         */
        playerChanged(config) {
            this.$emit('update', { player: config });
        },

        /**
         * Close modal
         *
         */
        closeModal() {
            this.$emit(
                'close',
                {
                    editPlayerType: null,
                    showEmbed: false,
                    player: null,
                    trackNo: null,
                }
            );
        },
    },
};
</script>
